/** Presentation of a tenant lifecycle request (tenant retirement): who asked and why — the quote
 *  the Lifecycle tab's open request and every decision dialog lead with — the decision in words,
 *  and what followed an approval (the close / reopen follow-up steps). */
import { Box, Typography } from '@mui/material';

import { TENANT_REQUEST_KIND_LABELS, type TenantRequest } from '@/api/tenantRequests';
import { userLabel } from '@/features/onboarding-invites/inviteFormat';
import { When } from '@/features/tour-operators/sandbox/When';

/** "Close requested by maya@… · 5m ago" (the When carries the full date on hover). */
export function RequestedLine({ request }: { request: TenantRequest }) {
  return (
    <>
      {TENANT_REQUEST_KIND_LABELS[request.kind]} requested by{' '}
      {request.requested_by ? userLabel(request.requested_by) : 'a user no longer there'} ·{' '}
      <When iso={request.requested_ts} />
    </>
  );
}

/** The request as it was made: who, when, the reason quoted, and a release override's reason. */
export function LifecycleRequestQuote({ request }: { request: TenantRequest }) {
  const reason = request.lifecycle?.reason?.trim();
  const override = request.lifecycle?.override_reason?.trim();
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" component="div">
        <RequestedLine request={request} />
      </Typography>
      <Typography
        variant="body2"
        component="blockquote"
        sx={{
          m: 0,
          mt: 0.75,
          pl: 1.25,
          borderLeft: 3,
          borderColor: 'divider',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          ...(reason ? {} : { color: 'text.secondary', fontStyle: 'italic' }),
        }}
      >
        {reason || 'No reason given.'}
      </Typography>
      {override && (
        <Typography variant="body2" sx={{ mt: 0.75 }}>
          Waiting period overridden: {override}
        </Typography>
      )}
    </Box>
  );
}

/** The decision in a few words: "Approved by maya@…: <reason>", "Rejected by …: <note>",
 *  "Withdrawn by …", "Failed: <error>"; empty while pending. */
export function lifecycleDecisionText(r: TenantRequest): string {
  const who = r.decided_by ? ` by ${userLabel(r.decided_by)}` : '';
  const note = r.decision_note?.trim();
  switch (r.status) {
    case 'pending':
      return '';
    case 'rejected':
      return [`Rejected${who}`, note].filter(Boolean).join(': ');
    case 'withdrawn':
      return `Withdrawn${who}`;
    case 'superseded':
      return 'Replaced by a newer request';
    case 'failed':
      return [`Approved${who} — failed`, r.result?.error].filter(Boolean).join(': ');
    default:
      return [`Approved${who}`, note].filter(Boolean).join(': ');
  }
}

/** What followed an applied close / reopen, by step: "6 follow-up steps done", "5 done, 1 failed
 *  (revoke_mcp_grants: …), 1 still to run". Null before the request applied. */
export function cascadeSummary(r: TenantRequest): string | null {
  const steps = Object.entries(r.lifecycle?.cascades ?? {});
  if (steps.length === 0) return null;
  const done = steps.filter(([, s]) => s.ok === true).length;
  const failed = steps.filter(([, s]) => s.ok === false);
  const waiting = steps.filter(([, s]) => s.ok == null).length;
  if (failed.length === 0 && waiting === 0) {
    return `${done} follow-up step${done === 1 ? '' : 's'} done.`;
  }
  const parts = [`${done} done`];
  if (failed.length > 0) {
    const which = failed.map(([name, s]) => (s.error ? `${name}: ${s.error}` : name)).join('; ');
    parts.push(`${failed.length} failed (${which})`);
  }
  if (waiting > 0) parts.push(`${waiting} still to run`);
  return `Follow-up steps: ${parts.join(', ')}.`;
}
